import React, { useEffect, useMemo, useState } from 'react';

import {
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import {
  FirmRfiRequestSourceKind,
  FirmRfiRequest,
  getGetApiV5FirmsRfiRequestsUuidQueryKey,
  usePatchApiV5FirmsRfiRequestsUuid,
  usePostApiV5FirmsRfiRequestsUuidSendToClient,
  usePostApiV5FirmsRfiRequestsUuidRecall,
  usePostApiV5FirmsRfiRequestsUuidResend,
} from '@liscio/api';
import { SignedUrlPreview, useSignedUrl } from '@liscio/common';
import {
  Stack,
  Alert,
  Typography,
  TextField,
  Tag,
  Divider,
  styled,
  Button,
  Skeleton,
  Link,
  Icon,
  Tooltip,
} from '@liscio/ui';

import { SelectOwnerDropdown } from '../../../../components/SelectOwnerDropdown/SelectOwnerDropdown';
import { RequestRecipientSelector } from '../../../requests/components/RequestRecipientSelector';
import { RfiArchiveButton } from '../RfiArchiveButton';
import { statusLabels } from '../RfiRequestsTableCells/StatusCell';
import { RfiSentNotice } from '../RfiSentNotice';
import { RfiUnarchiveButton } from '../RfiUnarchiveButton';
import { RHFDateInput } from 'components';
import { toErrorObjectArray } from 'fetch-utils/api-errors';

const DATE_FORMAT = 'MMM d, yyyy';

// RFI Request status constants
const RFI_STATUS = {
  DRAFT: 'draft',
  RECALLED: 'recalled',
  SENT_TO_CLIENT: 'sent_to_client',
  OPENED: 'opened',
  IN_PROGRESS: 'in_progress',
  SUBMITTED: 'submitted',
  ARCHIVED: 'archived',
} as const;

// RFI Review status constants
const REVIEW_STATUS = {
  PENDING_REVIEW: 'pending_review',
  APPROVED: 'approved',
} as const;

const EDITABLE_STATUSES: readonly string[] = [
  RFI_STATUS.DRAFT,
  RFI_STATUS.RECALLED,
];
const RECALLABLE_STATUSES: readonly string[] = [
  RFI_STATUS.SENT_TO_CLIENT,
  RFI_STATUS.OPENED,
  RFI_STATUS.IN_PROGRESS,
];
const CAN_ADD_CONTACTS_STATUSES: readonly string[] = [
  ...EDITABLE_STATUSES,
  ...RECALLABLE_STATUSES,
];

type FormDataType = {
  title?: string;
  owner_employee_uuid?: string;
  due_date?: string;
  // Fields managed by RequestRecipientSelector
  assignedAccountId?: string;
  assignedContactIds?: string[];
};

// Helper function to format due date for API
// Returns date as yyyy-MM-dd string without time/timezone to avoid timezone conversion issues
const formatDueDateForApi = (dueDate?: string): string => {
  if (!dueDate) return '';
  // Already in yyyy-MM-dd format, return as-is
  return dueDate;
};

// Helper function to build update request payload
const buildUpdatePayload = (formData: FormDataType) => {
  const isoDate = formatDueDateForApi(formData.due_date);
  return {
    title: formData.title,
    ...(formData.owner_employee_uuid && {
      owner_employee_uuid: formData.owner_employee_uuid,
    }),
    ...(isoDate && { due_date: isoDate }),
    assigned_account_id: formData.assignedAccountId || null,
    contact_uuids: formData.assignedContactIds || [],
  };
};

const InputLabelStyled = styled(InputLabel)(() => ({
  fontSize: '0.75rem',
  fontWeight: 600,
}));

const Wrapper = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  height: '100%',
  width: '100%',
}));

interface RfiRequestInfoSidebarProps {
  request: FirmRfiRequest | undefined;
  isLoading: boolean;
  onSaveSuccess?: () => void;
  showCancelButton?: boolean;
  onCancel?: () => void;
}

export const RfiRequestInfoSidebar: React.FC<RfiRequestInfoSidebarProps> = ({
  request,
  isLoading,
  onSaveSuccess,
  showCancelButton = false,
  onCancel,
}) => {
  const queryClient = useQueryClient();
  const { mutateAsync: updateRequest, isLoading: isSaving } =
    usePatchApiV5FirmsRfiRequestsUuid();

  const { mutateAsync: sendRequest, isLoading: isSending } =
    usePostApiV5FirmsRfiRequestsUuidSendToClient();

  const { mutateAsync: recallRequest, isLoading: isRecalling } =
    usePostApiV5FirmsRfiRequestsUuidRecall();

  const { mutateAsync: resendRequest, isLoading: isResending } =
    usePostApiV5FirmsRfiRequestsUuidResend();

  // Feature flag: Send to client functionality enabled for this firm
  const { enableRfiSystemClient = false } = useFlags();

  // Combined loading state for all operations
  const isOperationInProgress =
    isRecalling || isSaving || isSending || isResending;

  // Request is editable only in draft or recalled status
  const isEditable = EDITABLE_STATUSES.includes(request?.status || '');

  // Request is recallable when sent to client but not yet submitted/archived
  const isRecallable = RECALLABLE_STATUSES.includes(request?.status || '');

  // Request allows adding contacts (not archived/submitted)
  const canAddContacts = CAN_ADD_CONTACTS_STATUSES.includes(
    request?.status || ''
  );

  // Archived requests are read-only
  const isArchived = request?.status === RFI_STATUS.ARCHIVED;

  const [pdfToken, setPdfToken] = useState<string | null>(null);
  const [isRecallDialogOpen, setIsRecallDialogOpen] = useState(false);
  const {
    signedUrlData,
    setRetrievalToken,
    refreshSignedUrl,
    clearSignedUrl,
    isLoading: isSignedUrlLoading,
  } = useSignedUrl();

  const handleSourceFileClick = async (token: string | null | undefined) => {
    if (token) {
      setPdfToken(token);
      setRetrievalToken(token);
      await refreshSignedUrl();
    }
  };

  const { handleSubmit, register, control, reset, formState, watch } =
    useForm<FormDataType>({
      mode: 'onChange',
      defaultValues: {
        title: '',
        owner_employee_uuid: '',
        due_date: '',
        assignedAccountId: '',
        assignedContactIds: [],
      },
    });

  const watchedDueDate = watch('due_date');
  const watchedAccountId = watch('assignedAccountId');
  const watchedContactIds = watch('assignedContactIds') as string[] | undefined;

  // Check if new contacts have been added (for sent requests)
  const hasNewContacts = useMemo(() => {
    const originalContactIds =
      request?.assigned_contacts?.map((c) => c.uuid) || [];
    return (
      watchedContactIds?.some((id) => !originalContactIds.includes(id)) || false
    );
  }, [request?.assigned_contacts, watchedContactIds]);

  // Are send requirements met? (approved + has all required fields)
  const isDraftReadyToSend = useMemo(() => {
    if (request?.review_status !== REVIEW_STATUS.APPROVED) return false;

    return (
      Boolean(watchedDueDate) &&
      Boolean(watchedAccountId) &&
      (watchedContactIds?.length ?? 0) > 0
    );
  }, [
    request?.review_status,
    watchedDueDate,
    watchedAccountId,
    watchedContactIds,
  ]);

  // Will clicking save trigger emails to clients?
  // Only true if enableRfiSystemClient feature flag is enabled
  const willSendEmails = useMemo(() => {
    // Feature flag must be enabled to send to clients
    if (!enableRfiSystemClient) return false;

    const status = request?.status;

    if (status === RFI_STATUS.DRAFT && isDraftReadyToSend) return true;
    if (status === RFI_STATUS.RECALLED) return true;
    if (isRecallable && hasNewContacts) return true;

    return false;
  }, [
    enableRfiSystemClient,
    request?.status,
    isDraftReadyToSend,
    isRecallable,
    hasNewContacts,
  ]);

  // Button enabled state
  const isButtonEnabled = useMemo(() => {
    if (isOperationInProgress) return false;

    const status = request?.status;

    // Draft: enabled if dirty (can save) or ready to send
    if (status === RFI_STATUS.DRAFT) {
      return formState.isDirty || isDraftReadyToSend;
    }

    // Recalled: always enabled
    if (status === RFI_STATUS.RECALLED) return true;

    // Sent/Opened/In Progress: enabled only when dirty
    if (isRecallable) return formState.isDirty;

    // Submitted: enabled when dirty (owner, title, due date still editable)
    if (status === RFI_STATUS.SUBMITTED) return formState.isDirty;

    // Archived: always disabled
    return false;
  }, [
    isOperationInProgress,
    request?.status,
    formState.isDirty,
    isDraftReadyToSend,
    isRecallable,
  ]);

  // Button label
  const buttonLabel = useMemo(() => {
    if (isOperationInProgress) return 'Saving...';
    return willSendEmails ? 'Save & Send' : 'Save';
  }, [isOperationInProgress, willSendEmails]);

  // Validation errors for disabled button tooltip (draft only)
  const sendValidationErrors = useMemo(() => {
    if (request?.status !== RFI_STATUS.DRAFT) return '';
    if (request?.review_status !== REVIEW_STATUS.APPROVED)
      return 'Request must be approved';

    const errors: string[] = [];
    if (!watchedDueDate) errors.push('Due date is required');
    if (!watchedAccountId) errors.push('Account is required');
    if (!watchedContactIds?.length) errors.push('Contact is required');
    return errors.join(' | ');
  }, [
    request?.status,
    request?.review_status,
    watchedDueDate,
    watchedAccountId,
    watchedContactIds,
  ]);

  // Show tooltip only for draft when not ready to send
  const buttonTooltip = useMemo(() => {
    if (request?.status === RFI_STATUS.DRAFT && !isDraftReadyToSend) {
      return sendValidationErrors;
    }
    return '';
  }, [request?.status, isDraftReadyToSend, sendValidationErrors]);

  useEffect(() => {
    if (request) {
      let dueDate = '';
      try {
        dueDate = request.due_date
          ? new Date(request.due_date).toISOString().split('T')[0]
          : '';
      } catch (error) {
        console.error('Error parsing date:', error);
      }

      reset({
        title: request.title || '',
        owner_employee_uuid: request.owner_employee_uuid || '',
        due_date: dueDate,
        // Initialize assignedAccountId from assigned_account for RecipientSelector
        assignedAccountId: request.assigned_account?.uuid || '',
        // assignedContactIds will be populated by RecipientSelector from assignedContacts prop
        assignedContactIds: request.assigned_contacts?.map((c) => c.uuid) || [],
      });
    }
  }, [request, reset]);

  // Unified save handler - saves and optionally sends based on status
  // Send operations are only performed when enableRfiSystemClient feature flag is enabled
  const handleSave = handleSubmit(async (formData: FormDataType) => {
    if (!request?.uuid) return;

    const status = request.status;
    let requestSaved = false;

    try {
      // Always save
      await updateRequest({
        uuid: request.uuid,
        data: buildUpdatePayload(formData),
      });
      requestSaved = true;

      // Invalidate queries to refresh the list and detail page
      queryClient.invalidateQueries({
        queryKey: ['/api/v5/firms/rfi/requests'],
      });
      queryClient.invalidateQueries({
        queryKey: getGetApiV5FirmsRfiRequestsUuidQueryKey(request.uuid),
      });

      // Send/Resend if applicable (only when feature flag is enabled)
      if (enableRfiSystemClient) {
        if (status === RFI_STATUS.DRAFT && isDraftReadyToSend) {
          await sendRequest({ uuid: request.uuid });
          toast.success('Request sent to client');
        } else if (status === RFI_STATUS.RECALLED) {
          await resendRequest({ uuid: request.uuid });
          toast.success('Request sent to client');
        } else if (isRecallable && hasNewContacts) {
          toast.success('Request saved and sent to new contacts.');
        } else {
          toast.success('Request updated successfully.');
        }
      } else {
        toast.success('Request updated successfully.');
      }

      onSaveSuccess?.();
    } catch (error) {
      const apiMessage = toErrorObjectArray(error).errors?.find(
        ({ message }) => message
      )?.message;
      toast.error(
        apiMessage ||
          (requestSaved
            ? 'Request saved but could not be sent.'
            : 'Error saving request.')
      );
      console.error('Error saving or sending request:', error);
    }
  });

  const handleRecallClick = () => {
    setIsRecallDialogOpen(true);
  };

  const handleRecallConfirm = async () => {
    if (!request?.uuid) return;
    setIsRecallDialogOpen(false);
    try {
      await recallRequest({ uuid: request.uuid });
      toast.success('Request recalled successfully.');
      onSaveSuccess?.();
    } catch (error) {
      toast.error('Failed to recall request.');
      console.error('Error recalling request:', error);
    }
  };

  const handleRecallCancel = () => {
    setIsRecallDialogOpen(false);
  };

  const isPendingReview =
    request?.review_status === REVIEW_STATUS.PENDING_REVIEW;

  if (isLoading) {
    return (
      <Wrapper px={2} py={2} gap={2}>
        <Skeleton width={80} height={32} />
        <Skeleton width="100%" height={40} />
        <Skeleton width="100%" height={60} />
        <Skeleton width="100%" height={60} />
        <Skeleton width="100%" height={60} />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <form style={{ height: '100%' }}>
        <Stack height="100%">
          <Stack flexGrow={1} gap={2} padding="1rem" overflow="auto">
            {/* Status Tags */}
            <Stack
              direction="row"
              gap={1}
              flexWrap="wrap"
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack
                direction="row"
                sx={{ gap: 1, flexWrap: 'wrap', alignItems: 'center' }}
              >
                <Tag
                  label={
                    request?.status
                      ? (statusLabels[request.status] ?? request.status).toUpperCase()
                      : 'OPEN'
                  }
                  sx={{ width: 'fit-content' }}
                />
                {isPendingReview && (
                  <Tag
                    label="PENDING REVIEW"
                    color="warning"
                    sx={{ width: 'fit-content' }}
                  />
                )}
              </Stack>
              <Stack direction="row" gap={1} alignItems="center">
                {isRecallable && (
                  <Tooltip
                    title="Recall request to remove contacts or change account"
                    placement="bottom"
                  >
                    <span>
                      <Button
                        variant="outlined"
                        color="warning"
                        onClick={handleRecallClick}
                        disabled={isOperationInProgress}
                        startIcon={<Icon icon="arrowRotateRightDuotone" />}
                      >
                        {isRecalling ? 'Recalling...' : 'Recall'}
                      </Button>
                    </span>
                  </Tooltip>
                )}
                {request?.status === RFI_STATUS.ARCHIVED && (
                  <RfiUnarchiveButton requestUuid={request.uuid} />
                )}
                {request?.status !== RFI_STATUS.ARCHIVED &&
                  request?.status !== RFI_STATUS.DRAFT && (
                    <RfiArchiveButton requestUuid={request?.uuid || ''} />
                  )}
              </Stack>
            </Stack>

            {/* Title and Description */}
            <Typography variant="h5">{request?.title}</Typography>
            {request?.description && (
              <Typography variant="body1" color="text.secondary">
                {request.description}
              </Typography>
            )}

            <Divider />

            {/* SOURCE FILE section */}
            {request?.source_document && (
              <Stack gap={1}>
                <InputLabelStyled>SOURCE FILE</InputLabelStyled>
                <Link
                  href="#"
                  onClick={() =>
                    handleSourceFileClick(
                      request.source_document?.retrieval_token
                    )
                  }
                  display="flex"
                  alignItems="start"
                  sx={{ wordBreak: 'break-word' }}
                  disabled={isSignedUrlLoading}
                >
                  <Icon icon="filePdf" sx={{ marginRight: '5px' }} />
                  {request.source_document?.filename}
                </Link>
              </Stack>
            )}

            {/* REQUEST TITLE field */}
            <Stack gap={1}>
              <InputLabelStyled>REQUEST TITLE</InputLabelStyled>
              <TextField
                variant="outlined"
                fullWidth
                disabled={isArchived}
                {...register('title')}
              />
            </Stack>

            {/* OWNER field */}
            <Stack gap={1}>
              <InputLabelStyled>OWNER</InputLabelStyled>
              <SelectOwnerDropdown
                control={control}
                name="owner_employee_uuid"
                disabled={isArchived}
              />
            </Stack>

            {/* DUE DATE field */}
            <Stack gap={1} data-tour-id="smart-tax-organizer-due-date">
              <InputLabelStyled>DUE DATE</InputLabelStyled>
              <RHFDateInput
                name="due_date"
                control={control}
                format={DATE_FORMAT}
                valueFormat="yyyy-MM-dd"
                minDate={new Date()}
                placeholder="Select due date"
                disabled={isArchived}
              />
            </Stack>

            {/* ASSIGNEES section */}
            <Stack gap={1} data-tour-id="smart-tax-organizer-assignees">
              <RequestRecipientSelector
                control={control}
                account={request?.assigned_account ?? undefined}
                assignedContacts={request?.assigned_contacts ?? undefined}
                disabled={!canAddContacts || isOperationInProgress}
                disableRemoval={!isEditable}
                helperMessage={
                  !isEditable
                    ? 'You can add contacts. Recall request to remove contacts or change account.'
                    : undefined
                }
                maxHeight="15rem"
              />
            </Stack>
            {request?.source_kind === FirmRfiRequestSourceKind.tax_return &&
              request.omission_summary && (
                <Alert severity="warning">
                  {request.omission_summary.count}{' '}
                  {request.omission_summary.count === 1
                    ? 'value was'
                    : 'values were'}{' '}
                  not extracted. Review the organizer before sending.
                </Alert>
              )}
          </Stack>

          <RfiSentNotice visible={isRecallable} sentAt={request?.sent_at} />

          {/* Save Button */}
          <Stack
            direction="row"
            justifyContent={showCancelButton ? 'space-evenly' : 'stretch'}
            gap={showCancelButton ? 2 : 0}
            padding={2}
            sx={(theme) => ({
              borderTop: `1px solid ${theme.palette.divider}`,
            })}
          >
            {showCancelButton && onCancel && (
              <Button
                variant="outlined"
                color="primary"
                onClick={onCancel}
                fullWidth
                disabled={isOperationInProgress}
              >
                Cancel
              </Button>
            )}
            <Tooltip
              title={buttonTooltip}
              placement="top"
              disableHoverListener={!buttonTooltip}
            >
              <span
                style={{ width: '100%' }}
                data-tour-id="smart-tax-organizer-save-send"
              >
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={!isButtonEnabled}
                  fullWidth
                >
                  {buttonLabel}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </form>
      {pdfToken && signedUrlData && (
        <SignedUrlPreview
          onClose={() => {
            setPdfToken(null);
            clearSignedUrl();
          }}
          retrievalToken={pdfToken}
          signedUrl={signedUrlData.signed_url}
          fileName={signedUrlData.filename ?? 'File'}
        />
      )}

      {/* Recall Confirmation Dialog */}
      <Dialog open={isRecallDialogOpen} onClose={handleRecallCancel}>
        <DialogTitle>Recall Request?</DialogTitle>
        <DialogContent>
          <Typography>
            The client will no longer be able to access this request. You can
            edit and resend it later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRecallCancel} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleRecallConfirm}
            variant="contained"
            color="warning"
            disabled={isRecalling}
          >
            {isRecalling ? 'Recalling...' : 'Recall'}
          </Button>
        </DialogActions>
      </Dialog>
    </Wrapper>
  );
};

export default RfiRequestInfoSidebar;
